import { useState } from 'react';
import { Stack } from '@mui/material';
import {
  ExpandableContentButton,
  ExpandableContentWrapper,
} from 'components/shared';
import { CanvasGroupLinkButton } from 'components/canvas/groups';
import {
  FixedStrikeTable,
  FixedStrikeProvider,
} from 'components/implied_vol/FixedStrikeMatrix';
import { FixedStrikeTableSettings } from 'components/implied_vol/FixedStrikeMatrix/FixedStrikeTableControls';
import { CanvasComponentConfig, CanvasContentId } from 'types/canvas';
import { DEFAULT_FIXED_STRIKE_CONFIG, FixedStrikeConfig } from 'types';
import { useRecoilValue } from 'recoil';
import { hiroSymbolsState } from 'states/hiro';
import CanvasSymSelector from './CanvasSymSelector';

interface CanvasFixedStrikeMatrixProps {
  id: number;
  sym: string;
  config: CanvasComponentConfig;
  onSymChange: (sym: string) => void;
  onConfigChange: (patch: Partial<CanvasComponentConfig>) => void;
}

const CanvasFixedStrikeMatrix = ({
  id,
  sym,
  config: initialConfig,
  onSymChange,
  onConfigChange,
}: CanvasFixedStrikeMatrixProps) => {
  const hiroSymbols = useRecoilValue(hiroSymbolsState);
  const symOptions = Array.from(hiroSymbols)
    .sort()
    .map((s) => ({ symbol: s }));
  const [config, setConfig] = useState<FixedStrikeConfig>(() => ({
    ...DEFAULT_FIXED_STRIKE_CONFIG,
    ...(initialConfig as Partial<FixedStrikeConfig>),
  }));

  const handleConfigChange = (patch: Partial<FixedStrikeConfig>) => {
    setConfig((prev) => ({ ...prev, ...patch }));
    onConfigChange(patch as Partial<CanvasComponentConfig>);
  };

  const expandableContentButtonType = `${CanvasContentId.FIXED_STRIKE_MATRIX}-${id}`;

  return (
    <ExpandableContentWrapper
      type={expandableContentButtonType}
      styleOverrides={{ p: 0, height: '100%' }}
    >
      <FixedStrikeProvider
        selectedSym={sym}
        config={config}
        onConfigChange={handleConfigChange}
      >
        <Stack direction="column" spacing={1} sx={{ width: '100%' }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ px: 1, py: 0.5, justifyContent: 'flex-end', width: '100%' }}
          >
            <CanvasSymSelector
              sym={sym}
              options={symOptions}
              onSymChange={onSymChange}
            />
            <FixedStrikeTableSettings />
            <CanvasGroupLinkButton
              componentId={id}
              contentId={CanvasContentId.FIXED_STRIKE_MATRIX}
            />
            <ExpandableContentButton type={expandableContentButtonType} />
          </Stack>
          <FixedStrikeTable />
        </Stack>
      </FixedStrikeProvider>
    </ExpandableContentWrapper>
  );
};

export default CanvasFixedStrikeMatrix;
